// src/hooks/useSearch.js
import { useState, useCallback } from 'react';
import { apiService } from '../services/api';

const PAGE_SIZE = 10;

export const EMPTY_FILTERS = { status: '', packer: '', frosting: '', tag: [], minutes: '' };

export const useSearch = () => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const applyResponse = (response) => {
    if (response && response.results !== undefined) {
      setAnalyses(response.results);
      setTotalResults(response.total || 0);
      setTotalPages(response.total_pages || 0);
    } else {
      setAnalyses([]);
      setTotalResults(0);
      setTotalPages(0);
    }
  };

  // Fetch the browse list (no query). Accepts optional activeFilters to avoid
  // stale-closure issues when called immediately after setFilters().
  const fetchList = useCallback(async (page = 1, activeFilters) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.listAnalyses(page, PAGE_SIZE, activeFilters ?? filters);
      applyResponse(response);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching analyses:', err);
      setError('Failed to load analyses. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Perform a search with specific query syntax
  const performSearch = useCallback(async (query, page = 1) => {
    if (!query.trim()) {
      setSearchQuery('');
      return fetchList(1);
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.searchAnalyses(query, page, PAGE_SIZE);
      applyResponse(response);
      setSearchQuery(query);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error searching analyses:', err);
      setError('Search failed. Please try again with a valid query format (field:value).');
    } finally {
      setLoading(false);
    }
  }, [fetchList]);

  // Navigate to a specific page (respects current mode: browse or search)
  const goToPage = useCallback((page) => {
    if (searchQuery.trim()) {
      performSearch(searchQuery, page);
    } else {
      fetchList(page);
    }
  }, [searchQuery, performSearch, fetchList]);

  return {
    analyses,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    currentPage,
    totalResults,
    totalPages,
    pageSize: PAGE_SIZE,
    filters,
    setFilters,
    resetFilters,
    performSearch,
    fetchList,
    goToPage,
  };
};
